import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import RatingStars from '../LeadsTable/RatingStars/RatingStars';
import StatusBadge from '../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/StatusBadge/StatusBadge';
import ProspectedBadge from '../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/ProspectedBadge/ProspectedBadge';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import { SavedLead, updateLead, LeadInsertData } from '../../../../services/leadsService';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { COMMON_COUNTRIES } from '../../../../constants/countries';

interface EditLeadModalProps {
    isOpen: boolean;
    onClose: () => void;
    lead: SavedLead | null;
    onLeadUpdated: (updatedLead: SavedLead) => void;
}

const EditLeadModal: React.FC<EditLeadModalProps> = ({
    isOpen,
    onClose,
    lead,
    onLeadUpdated,
}) => {
    const { t } = useTranslation();

    // Estado do formulário
    const [formData, setFormData] = useState({
        company_name: '',
        website: '',
        phone_number: '',
        email: '',
        category: '',
        address: '',
        location: '',
        rating: 0,
        prospected: false,
    });

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Atualizar formData quando lead mudar
    useEffect(() => {
        if (lead) {
            setFormData({
                company_name: lead.company_name || '',
                website: lead.website || '',
                phone_number: lead.phone_number || '',
                email: lead.email || '',
                category: lead.category || '',
                address: lead.address || '',
                location: lead.location || '',
                rating: lead.rating || 0,
                prospected: lead.prospected || false,
            });
        }
    }, [lead]);

    // Se não estiver aberto ou sem lead, não renderiza NADA, mas os hooks já foram chamados acima
    if (!isOpen || !lead) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveClick = () => {
        // Validação básica
        if (!formData.company_name.trim()) {
            toast.error(t('O nome da empresa é obrigatório.'));
            return;
        }
        setIsConfirmModalOpen(true);
    };

    const handleConfirmSave = async () => {
        setIsSaving(true);
        try {
            const updates: Partial<LeadInsertData> = {
                company_name: formData.company_name.trim(),
                website: formData.website.trim() || undefined,
                phone_number: formData.phone_number.trim() || undefined,
                email: formData.email.trim() || undefined,
                category: formData.category.trim() || undefined,
                address: formData.address.trim() || undefined,
                location: formData.location || undefined,
                rating: formData.rating,
                prospected: formData.prospected,
            };

            const updatedLead = await updateLead(lead.id, updates);
            onLeadUpdated(updatedLead);
            toast.success(t('Lead atualizado com sucesso!'));
            setIsConfirmModalOpen(false);
            onClose();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t('Erro ao atualizar lead.');
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={onClose}
            >
                <motion.div
                    className="relative w-full max-w-2xl rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700/50 p-6 flex flex-col gap-6 max-h-[90vh] overflow-y-auto"
                    initial={{ y: 50, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 50, opacity: 0, scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-start justify-between border-b border-gray-200 dark:border-gray-700/50 pb-4">
                        <div className="flex flex-col gap-1">
                            <h2 className="text-2xl font-bold text-[#181411] dark:text-white">{t('Editar Lead')}</h2>
                            <div className="flex items-center gap-3 mt-1">
                                <RatingStars rating={lead.rating} />
                                <StatusBadge status={lead.whatsapp_status} whatsapp={lead.whatsapp_status === 'Validado'} />
                                <ProspectedBadge prospected={lead.prospected} />
                            </div>
                        </div>
                        <motion.button
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
                            whileHover={{ rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
                        </motion.button>
                    </div>

                    {/* Form */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {/* Nome da Empresa */}
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('dashboard.detail_company')} *
                            </label>
                            <input
                                type="text"
                                name="company_name"
                                value={formData.company_name}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#181411] dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Website */}
                        <div className="col-span-1">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('dashboard.detail_website')}
                            </label>
                            <input
                                type="text"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="example.com"
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#181411] dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Telefone */}
                        <div className="col-span-1">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('dashboard.detail_phone')}
                            </label>
                            <input
                                type="text"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                placeholder="+55 11 99999-9999"
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#181411] dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Email */}
                        <div className="col-span-1">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('leads.detail_email')}
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="email@example.com"
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#181411] dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Categoria */}
                        <div className="col-span-1">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('leads.detail_category')}
                            </label>
                            <input
                                type="text"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                placeholder={t('Ex: Restaurante, Loja')}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#181411] dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* País (Country) */}
                        <div className="col-span-1">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('lead_form.country')}
                            </label>
                            <select
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#181411] dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                                <option value="">{t('lead_form.country_placeholder')}</option>
                                {COMMON_COUNTRIES.map(country => (
                                    <option key={country.code} value={country.name}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Rating (Estrelas) */}
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('lead_form.rating')}
                            </label>
                            <div className="flex items-center space-x-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                                        className="p-1 transition-transform hover:scale-110"
                                    >
                                        <span className={`material-symbols-outlined text-2xl ${star <= formData.rating
                                            ? 'text-yellow-400'
                                            : 'text-gray-300 dark:text-gray-600'
                                            }`}>
                                            {star <= formData.rating ? 'star' : 'star_outline'}
                                        </span>
                                    </button>
                                ))}
                                {formData.rating > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, rating: 0 }))}
                                        className="ml-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                    >
                                        {t('lead_form.clear_rating')}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Endereço */}
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                {t('leads.detail_address')}
                            </label>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                rows={2}
                                placeholder={t('Endereço completo')}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#181411] dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Já Prospectado Toggle */}
                        <div className="col-span-2 flex items-center justify-between pt-2 pb-1">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('Marcar como prospectado')}
                                <span className="block text-xs text-gray-500 dark:text-gray-400 font-normal mt-0.5">
                                    {t('Indica que este lead já recebeu mensagens')}
                                </span>
                            </label>
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, prospected: !prev.prospected }))}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.prospected ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                            >
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.prospected ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700/50">
                        <motion.button
                            onClick={onClose}
                            className="flex h-10 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700/50 text-[#181411] dark:text-gray-300 text-sm font-bold px-4 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {t('modals.cancel')}
                        </motion.button>
                        <motion.button
                            onClick={handleSaveClick}
                            disabled={isSaving}
                            className="flex h-10 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-primary text-white text-sm font-bold px-6 hover:opacity-90 transition-opacity disabled:opacity-50"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {isSaving ? t('Salvando...') : t('Salvar Alterações')}
                        </motion.button>
                    </div>
                </motion.div>
            </motion.div>

            {/* Modal de Confirmação */}
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleConfirmSave}
                title={t('Confirmar Alterações')}
                message={t('Tem certeza que deseja salvar as alterações feitas neste lead?')}
                confirmText={t('Sim, Salvar')}
                cancelText={t('modals.cancel')}
            />
        </>
    );
};

export default EditLeadModal;
